import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import { useLazyQuery } from "@apollo/client/react";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import {
  ASSETS_COLLECTION_BY_IDS_QUERY,
  CREATE_INVENTORY_TRANSACTION_MUTATION,
} from "../../constants/graphql";
import useMutation from "../../hooks/useMutation";
import useNotification from "../../hooks/notification";
import StorageLocationAutocomplete from "../../views/capture/RackLocations/StorageLocationAutocomplete/StorageLocationAutocomplete";
import { useProjectInventory } from "../../contexts/projectInventory";
import MoveConfirmationModal from "../modals/MoveLocationsModal/MoveConfirmationModal";
import MobileLocationPromptModal from "./MobileLocationPromptModal";

const validationSchema = Yup.object().shape({
  rackLocation: Yup.object().shape({
    warehouse: Yup.object()
      .nullable()
      .required("Please select a storage location"),
    warehouse_locations: Yup.array()
      .min(1, "Please select a storage location")
      .required("Storage location is required"),
  }),
});

export default function BulkInventoryMoveModal({ selectedIds, onClose, onComplete }) {
  const { notifySuccess, notifyError } = useNotification();
  const { projectId } = useProjectInventory();
  const [loading, setLoading] = useState(false);
  const [fetchingAssets, setFetchingAssets] = useState(true);
  const [assets, setAssets] = useState([]);
  const [progress, setProgress] = useState({ current: 0, total: 0, currentAsset: "" });
  const [errors, setErrors] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationData, setConfirmationData] = useState({ locations: [], warehouseName: "" });
  const [showMobileLocationPrompt, setShowMobileLocationPrompt] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);

  const [
    getSelectedAssets,
    { loading: queryLoading, data: queryData, error: queryError, called },
  ] = useLazyQuery(ASSETS_COLLECTION_BY_IDS_QUERY);
  const [createInventoryTransaction] = useMutation(CREATE_INVENTORY_TRANSACTION_MUTATION);

  // Fetch asset data when modal opens
  useEffect(() => {
    getSelectedAssets({ variables: { assetIds: selectedIds } });
  }, [selectedIds, getSelectedAssets]);

  // Handle query results
  useEffect(() => {
    if (called && !queryLoading) {
      if (queryError) {
        notifyError("Failed to load asset data");
        onClose();
        return;
      }

      if (queryData) {
        const fetchedAssets = queryData?.assetsCollection?.collection || [];

        if (fetchedAssets.length === 0) {
          notifyError("No assets found for the selected IDs");
          onClose();
          return;
        }

        setAssets(fetchedAssets);
        setFetchingAssets(false);
      }
    }
  }, [called, queryLoading, queryData, queryError, notifyError, onClose]);

  const calculateRackLocationsChanges = (
    oldRackLocations,
    newRackLocation,
    keepMobileLocation = true
  ) => {
    // For bulk inventory move, preserve existing quantities and other fields, only update warehouse_locations and warehouse
    return oldRackLocations.map(location => {
      const updated = {
        ...location,
        warehouse_locations: newRackLocation.warehouse_locations,
        warehouse: newRackLocation.warehouse,
      };

      // Only update mobile_warehouse_location if a new one is explicitly selected
      if (newRackLocation.mobile_warehouse_location?.id) {
        updated.mobile_warehouse_location = newRackLocation.mobile_warehouse_location;
      } else if (!keepMobileLocation) {
        updated.mobile_warehouse_location = null;
      }

      return updated;
    });
  };

  const executeMove = async (values, keepMobileLocation) => {
    setLoading(true);
    setProgress({ current: 0, total: assets.length, currentAsset: "" });
    setErrors([]);

    try {
      const newErrors = [];
      let lastSuccessfulResponse = null;

      // Process each asset sequentially
      for (let i = 0; i < assets.length; i++) {
        const asset = assets[i];
        setProgress({
          current: i + 1,
          total: assets.length,
          currentAsset: asset.itemId || asset.assetId || asset.id,
        });

        try {
          // Preserve existing quantities and other fields, only update warehouse_locations and warehouse
          const newRackLocations = asset.rackLocations.map(location => {
            const updated = {
              ...location,
              warehouse_locations: values.rackLocation.warehouse_locations,
              warehouse: values.rackLocation.warehouse,
            };

            // Only update mobile_warehouse_location if a new one is explicitly selected
            if (values.rackLocation.mobile_warehouse_location?.id) {
              updated.mobile_warehouse_location = values.rackLocation.mobile_warehouse_location;
            } else if (!keepMobileLocation) {
              updated.mobile_warehouse_location = null;
            }

            return updated;
          });

          const payload = {
            code: asset.assetId,
            id: asset.id,
            quantity: 0,
            oldRackLocations: asset.rackLocations || [],
            rackLocations: newRackLocations,
            rackLocationsChanges: calculateRackLocationsChanges(
              asset.rackLocations || [],
              values.rackLocation,
              keepMobileLocation
            ),
            notes: "",
            senderName: "",
            carrierName: "",
            trackingNumber: "",
            orderNumber: "",
            poNumber: "",
            actionSource: "bulk_upload",
          };

          const response = await createInventoryTransaction({ variables: payload });
          lastSuccessfulResponse = response?.data?.createInventoryTransaction;
        } catch (error) {
          newErrors.push({
            assetId: asset.assetId || asset.id,
            error: error.message || "Unknown error",
          });
        }
      }

      // 3. Show results
      if (newErrors.length === 0) {
        // All transfers successful - show confirmation modal
        if (lastSuccessfulResponse?.asset) {
          const {
            rackLocations = [],
            warehousesFromRackLocations = [],
          } = lastSuccessfulResponse.asset;
          const locationNames = rackLocations
            .flatMap(rl => (rl.warehouse_locations || []).map(loc => loc.location_name))
            .filter(Boolean);
          const warehouseName = warehousesFromRackLocations[0]?.name || "";

          const mobileLocation = rackLocations.find(rl => rl.mobile_warehouse_location)
            ?.mobile_warehouse_location;

          setConfirmationData({ locations: locationNames, warehouseName, mobileLocation });
          setShowConfirmation(true);
          notifySuccess(`Successfully transferred ${assets.length} assets`);
        } else {
          notifySuccess(`Successfully transferred ${assets.length} assets`);
          onComplete();
        }
      } else {
        setErrors(newErrors);
        const successCount = assets.length - newErrors.length;
        if (successCount > 0) {
          if (lastSuccessfulResponse?.asset) {
            const {
              rackLocations = [],
              warehousesFromRackLocations = [],
            } = lastSuccessfulResponse.asset;
            // warehouse_locations is now an array - flatten all locations
            const locationNames = rackLocations
              .flatMap(rl => (rl.warehouse_locations || []).map(loc => loc.location_name))
              .filter(Boolean);
            const warehouseName = warehousesFromRackLocations[0]?.name || "";

            const mobileLocation = rackLocations.find(rl => rl.mobile_warehouse_location)
              ?.mobile_warehouse_location;

            setConfirmationData({ locations: locationNames, warehouseName, mobileLocation });
            setShowConfirmation(true);
            notifySuccess(
              `Successfully transferred ${successCount} assets. ${newErrors.length} failed.`
            );
          } else {
            notifySuccess(
              `Successfully transferred ${successCount} assets. ${newErrors.length} failed.`
            );
          }
        } else {
          notifyError("All transfers failed");
        }
      }
    } catch (error) {
      notifyError("Failed to complete bulk inventory move");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async values => {
    const hasMobileLocation = assets.some(asset =>
      (asset.rackLocations || []).some(rl => rl.mobile_warehouse_location)
    );

    if (hasMobileLocation) {
      setPendingValues(values);
      setShowMobileLocationPrompt(true);
    } else {
      executeMove(values, true);
    }
  };

  const handlePromptChoice = keep => {
    setShowMobileLocationPrompt(false);
    if (pendingValues) {
      executeMove(pendingValues, keep);
      setPendingValues(null);
    }
  };

  const handleConfirmationClose = () => {
    setShowConfirmation(false);
    onClose();
  };

  if (showMobileLocationPrompt) {
    return (
      <MobileLocationPromptModal
        assets={assets}
        onRemove={() => handlePromptChoice(false)}
        onKeep={() => handlePromptChoice(true)}
        onClose={() => setShowMobileLocationPrompt(false)}
      />
    );
  }

  if (showConfirmation) {
    return (
      <MoveConfirmationModal
        open={showConfirmation}
        onClose={handleConfirmationClose}
        itemType={assets.length === 1 ? "item" : "items"}
        locations={confirmationData.locations}
        warehouseName={confirmationData.warehouseName}
        mobileLocation={confirmationData.mobileLocation}
      />
    );
  }

  if (fetchingAssets) {
    return (
      <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "center" }}>
          <Typography fontSize={24} fontWeight={500} color="textPrimary" align="center">
            Loading Assets...
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" py={3}>
            <LinearProgress style={{ width: "100%", marginBottom: 16 }} />
            <Typography variant="body2" color="textSecondary">
              Fetching asset data for {selectedIds.length} selected assets...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "center" }}>
        <Typography fontSize={24} fontWeight={500} color="textPrimary" align="center">
          Bulk Inventory Move
        </Typography>
      </DialogTitle>

      <Formik
        initialValues={{
          rackLocation: {
            warehouse: null,
            warehouse_locations: [],
            mobile_warehouse_location: null,
          },
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, isValid, errors, touched }) => {
          return (
            <Form>
              <DialogContent>
                <Box mb={2} display="flex" justifyContent="center">
                  <Typography variant="body1" color="textSecondary" align="center">
                    Select the new storage location for all {selectedIds.length} selected assets.
                  </Typography>
                </Box>

                <StorageLocationAutocomplete
                  value={values.rackLocation}
                  onChange={locationData => setFieldValue("rackLocation", locationData)}
                  projectId={projectId}
                  disabled={loading}
                />

                {loading && (
                  <Box mt={2}>
                    <Typography variant="body2" gutterBottom>
                      Processing {progress.current} of {progress.total} assets...
                    </Typography>
                    {progress.currentAsset && (
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Current: {progress.currentAsset}
                      </Typography>
                    )}
                    <LinearProgress
                      variant="determinate"
                      value={(progress.current / progress.total) * 100}
                    />
                  </Box>
                )}

                {errors.length > 0 && (
                  <Box mt={2}>
                    <Typography variant="body2" color="error" gutterBottom>
                      Failed transfers:
                    </Typography>
                    {errors.map((error, index) => (
                      <Typography key={index} variant="body2" color="error">
                        • {error.assetId}: {error.error}
                      </Typography>
                    ))}
                  </Box>
                )}
              </DialogContent>

              <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={onClose} disabled={loading}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={!values.rackLocation?.warehouse?.id || loading}
                >
                  {loading ? "Processing..." : "Submit"}
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
}
